import {
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { catchError, of } from 'rxjs';

// The access read and its chip come from the Access & Controls feature on purpose: one patient's
// portal access state must read the same wherever staff meet it, and a second copy of either here
// is how the two screens start disagreeing. (Same cross-feature arrangement the dashboard has with
// `requests`.)
import { PatientPortalAccess } from '../../../access-controls/models/patient-portal-access';
import { PatientPortalAccessService } from '../../../access-controls/services/patient-portal-access.service';
import { getAccessStatusBadge } from '../../../access-controls/utils/access-status-badge.utils';
import { IconName } from '../../../shared/components/icon/icon-data.generated';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { ProgressSpinnerComponent } from '../../../shared/components/progress-spinner/progress-spinner.component';
import {
  formatLocalDate,
  formatLocalTime,
  formatWallClockDate,
  formatWallClockTime,
} from '../../../shared/utils/date-time/date-time.utils';
import { logHttpError } from '../../../shared/utils/logging/http-error-logging.utils';
import { EMPTY_CELL } from '../../../shared/utils/table/table.utils';
import {
  DisclosedAppointment,
  DisclosedRecord,
  PatientPortalAccessLogDetail,
} from '../../models/access-log-detail';
import { EventType } from '../../models/event-type';
import { ActivityLogsService } from '../../services/activity-logs.service';
import { getActorRoleLabel } from '../../utils/actor-role/actor-role.utils';
import {
  AppointmentStatusBadgeKind,
  getAppointmentStatusBadgeKind,
} from '../../utils/appointment-status/appointment-status.utils';
import { getErrorStatus } from '../../utils/http-error/http-error.utils';
import { toTypeBadgeClass } from '../../utils/type-badge/type-badge.utils';

const NOT_FOUND_MESSAGE =
  'This audit entry may have been removed, or the link you followed is no longer valid.';
const LOAD_ERROR_MESSAGE = 'Something went wrong while loading this audit entry. Please try again.';

/**
 * Heading chip label per event type. The detail response carries the raw `eventTypeId` but not the
 * search row's composed `typeCategory`, so the chip is named here — from the same vocabulary the
 * feed's "Type" column uses, so an event reads the same word and the same colour in both places.
 * An id outside the enum yields no chip rather than a guess.
 */
const EVENT_TYPE_BADGES: Record<number, string> = {
  [EventType.View]: 'View',
  [EventType.Print]: 'Print',
  [EventType.Download]: 'Export',
  [EventType.Transmit]: 'Transmit',
};

/**
 * The past-tense verb the subtitle opens with, per event type. A disclosure ledger records four
 * distinct actions and the chip beside the title already names all four, so the subtitle cannot
 * hardcode one of them: "Viewed …" under a "Print" chip asserts an access that did not happen.
 *
 * {@link FALLBACK_EVENT_VERB} covers an id outside the enum — the neutral verb the sibling
 * disclosure screen uses, which is true of every event type rather than a guess at which one.
 */
const EVENT_TYPE_VERBS: Record<number, string> = {
  [EventType.View]: 'Viewed',
  [EventType.Print]: 'Printed',
  [EventType.Download]: 'Exported',
  [EventType.Transmit]: 'Transmitted',
};

const FALLBACK_EVENT_VERB = 'Accessed';

/** The generic disclosed-records screen, offered as a way out of an empty appointments table. */
const DISCLOSURE_DETAIL_ROUTE = '/disclosure-detail';

/** A single disclosed appointment, projected for the table. */
interface AppointmentRow {
  id: number;
  /**
   * Stable per-row identity for `@for`. Not `id` alone: `recordId` is the source table's own
   * IDENTITY value, and the two appointment tables number independently, so a native and a
   * Meditech appointment in the same event can collide on it — and mixing both sources in one
   * event is the normal case here, not an edge case. A duplicate key makes Angular reuse the wrong
   * DOM node on re-render, which on this screen means one appointment's data under another's row.
   */
  trackKey: string;
  /** Wall-clock date of the appointment, e.g. "Jul 14, 2026". */
  date: string;
  /** Wall-clock time, shown as subtext under the date. */
  time: string;
  providerName: string;
  /**
   * Always empty in practice — no source table carries a specialty. Kept so the cell lights up if
   * the schema grows one, rather than the template having to change too.
   */
  providerSpecialty: string;
  appointmentType: string;
  locationName: string;
  /** Secondary location text, "City, ST". */
  locationDetail: string;
  /** The exact status label from the source table; always rendered, chip or no chip. */
  statusLabel: string;
  /** Chip colour, or null when the API declined to categorise this status. */
  statusKind: AppointmentStatusBadgeKind | null;
  isDeleted: boolean;
}

/**
 * The Audit Log's appointment drill-down: which appointments were visible to a patient or proxy
 * during one "Viewed Appointments" session.
 *
 * Reads the same `PatientPortalAccessLogs/{id}` endpoint as the generic disclosure detail — an
 * appointment disclosure is an ordinary access-log event — but renders the appointment-specific
 * columns the API hangs off `DisclosedRecord.appointment`. A record with no `appointment` payload
 * is skipped rather than half-rendered: it means the event mixed in a non-appointment source, and
 * the generic screen is where that belongs.
 */
@Component({
  selector: 'app-appointment-detail',
  standalone: true,
  imports: [RouterLink, IconComponent, ProgressSpinnerComponent],
  templateUrl: './appointment-detail.html',
  styleUrl: './appointment-detail.scss',
})
export class AppointmentDetail implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly activityLogsService = inject(ActivityLogsService);
  private readonly portalAccessService = inject(PatientPortalAccessService);

  readonly logId = Number(this.route.snapshot.paramMap.get('logId') ?? 0);

  readonly isLoading = signal(true);
  readonly loadError = signal<string | null>(null);
  readonly notFound = signal(false);
  readonly detail = signal<PatientPortalAccessLogDetail | null>(null);

  readonly errorTitle = computed(() =>
    this.notFound() ? 'Audit entry not found' : 'Unable to load audit entry',
  );
  readonly errorIcon = computed<IconName>(() => (this.notFound() ? 'search' : 'warning'));

  /** Page title and the last breadcrumb crumb — "Appointments", as the API composes it. */
  readonly featureArea = computed(() => this.detail()?.featureArea ?? '');

  /** The subject patient, named in the breadcrumb and the Event Details card. */
  readonly patientName = computed(() => this.detail()?.patientDisplayName ?? '');
  readonly medicalRecordNumber = computed(() => this.detail()?.medicalRecordNumber ?? '');

  /**
   * The subject patient's Access & Controls screen, or null when the entry carries no usable
   * patient id — the name then stays plain text rather than linking to a not-found page.
   */
  readonly patientAccessRoute = computed(() => {
    const patientId = this.detail()?.patientId;
    return patientId && Number.isInteger(patientId) && patientId > 0
      ? ['/access-controls/patients', patientId]
      : null;
  });

  /**
   * The subject patient's portal access state, or null while it is unread or unreadable.
   *
   * A patient who has been blocked out of the portal must not read as Active on a screen where
   * staff are reviewing what was disclosed about them. The chip is the same one the Access &
   * Controls detail screen shows, from the same helper, so the two cannot disagree.
   */
  readonly portalAccess = signal<PatientPortalAccess | null>(null);

  /** The access read failed, as opposed to still being in flight. */
  readonly accessLoadFailed = signal(false);

  readonly accessStatusBadge = computed(() => getAccessStatusBadge(this.portalAccess()));

  /**
   * The access read has answered, one way or the other. The chip waits for it rather than showing
   * "Status unavailable" while the read is still in flight, which would read as a failure.
   */
  readonly hasAccessStatusSettled = computed(
    () => this.portalAccess() !== null || this.accessLoadFailed(),
  );

  /** Explains the neutral "Status unavailable" chip after a failed read. Null once a status loaded. */
  readonly accessStatusTitle = computed(() =>
    this.accessLoadFailed() ? "Portal access for this patient couldn't be loaded." : null,
  );

  readonly eventDate = computed(() => {
    const detail = this.detail();
    return detail ? formatLocalDate(detail.createdOnUtc) : '';
  });

  readonly eventTime = computed(() => {
    const detail = this.detail();
    return detail ? formatLocalTime(detail.createdOnUtc) : '';
  });

  /** Chip label beside the title, or null for an event type the client doesn't name. */
  readonly eventBadge = computed(() => {
    const detail = this.detail();
    return detail ? (EVENT_TYPE_BADGES[detail.eventTypeId] ?? null) : null;
  });

  /** Chip colour, keyed off the label so it can never disagree with the text it wraps. */
  readonly eventBadgeClass = computed(
    () => `action-badge--${toTypeBadgeClass(this.eventBadge() ?? '')}`,
  );

  /** Who performed the access, and in what capacity — the Event Details "Performed By" field. */
  readonly actorName = computed(() => this.detail()?.actingPortalUser?.displayName ?? '');

  /** The actor's relationship to the subject patient; see {@link getActorRoleLabel}. */
  readonly actorRole = computed(() => getActorRoleLabel(this.detail()?.actingPortalUser));

  /**
   * Subtitle: "{verb} {date} at {time} · {actor} ({relationship})", where the verb is the event's
   * own action rather than a fixed "Viewed" — see {@link EVENT_TYPE_VERBS}.
   */
  readonly subtitle = computed(() => {
    const detail = this.detail();
    if (!detail) {
      return '';
    }
    const verb = EVENT_TYPE_VERBS[detail.eventTypeId] ?? FALLBACK_EVENT_VERB;
    const when = `${verb} ${formatLocalDate(detail.createdOnUtc)} at ${formatLocalTime(detail.createdOnUtc)}`;
    const actor = detail.actingPortalUser;
    return actor ? `${when} · ${actor.displayName} (${this.actorRole()})` : when;
  });

  /** Precompiled explanation shown as a banner for increased-severity disclosures. */
  readonly severityNote = computed(() => this.detail()?.severityNote ?? null);

  /**
   * The appointments visible during the session.
   *
   * Only records carrying an `appointment` payload are projected. A "Viewed Appointments" event
   * routinely mixes native and Meditech rows — both of which have the payload — but the endpoint
   * also serves every other kind of disclosure, so a record without it is another source's and has
   * no columns this table can fill.
   */
  readonly rows = computed<AppointmentRow[]>(() =>
    // `flatMap` rather than `filter` + `map` so the payload is narrowed by the same check that
    // decides whether to keep the record. A `filter` doesn't narrow, which left `toRow` guarding
    // separately — and the two guards disagreed on an *absent* `appointment` key (`undefined !==
    // null` is true), so a response that omits the field instead of nulling it would have reached
    // the guard and thrown from inside a computed, taking the whole screen down.
    (this.detail()?.disclosedRecords ?? []).flatMap((record) =>
      record.appointment ? [this.toRow(record, record.appointment)] : [],
    ),
  );

  /**
   * Where to send someone whose event hydrated records this screen can't render.
   *
   * `resolveDisclosureDetailRoute` routes on a substring of the API's `feature`, so a feature that
   * merely contains "appointment" (an "Appointment Summary" document, a reminder message) lands
   * here while carrying only non-appointment records — an empty table with no way forward. The
   * generic screen can show exactly those records, so the empty state offers it rather than dead-
   * ending. Null when the event genuinely disclosed nothing, where the generic screen is just as
   * empty and the link would be a false promise.
   */
  readonly fallbackDetailRoute = computed(() => {
    const records = this.detail()?.disclosedRecords ?? [];
    return this.rows().length === 0 && records.length > 0
      ? `${DISCLOSURE_DETAIL_ROUTE}/${this.logId}`
      : null;
  });

  private readonly tableScroll = viewChild<ElementRef<HTMLElement>>('tableScroll');

  /**
   * Whether the table actually overflows its card — which is what makes the wrapper a scroll region
   * worth a tab stop.
   *
   * Measured rather than derived from the breakpoint: `overflow-x: auto` is applied by a media
   * query in the stylesheet, and restating that breakpoint here would be a second source of truth
   * that silently drifts. Above it nothing scrolls, so an unconditional `tabindex` would hand
   * keyboard users a stop that does nothing and announce "scrollable" for a region that isn't.
   */
  readonly isScrollable = signal(false);

  constructor() {
    effect((onCleanup) => {
      const element = this.tableScroll()?.nativeElement;
      if (!element) {
        this.isScrollable.set(false);
        return;
      }

      const measure = () => this.isScrollable.set(element.scrollWidth > element.clientWidth);
      measure();

      // Matches the queue tables' guard: jsdom has no ResizeObserver, and the initial measurement
      // above is the whole behaviour under test.
      if (typeof ResizeObserver === 'undefined') {
        return;
      }

      const observer = new ResizeObserver(() => measure());
      observer.observe(element);
      onCleanup(() => observer.disconnect());
    });
  }

  ngOnInit(): void {
    if (!Number.isInteger(this.logId) || this.logId <= 0) {
      this.showNotFound();
      return;
    }

    this.activityLogsService
      .getAccessLogById(this.logId)
      .pipe(
        catchError((error: unknown) => {
          // A missing record arrives either as a 404 or as a RecordNotFound envelope (handled
          // below, as a null emission); anything else is a genuine failure.
          if (getErrorStatus(error) === 404) {
            this.showNotFound();
          } else {
            this.notFound.set(false);
            this.loadError.set(LOAD_ERROR_MESSAGE);
            this.isLoading.set(false);
          }
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((detail) => {
        if (!detail) {
          // Either the catchError above already set the state, or the envelope carried a null
          // result for an id that doesn't exist — both read as "not found". Without this the
          // spinner would spin forever on a RecordNotFound envelope.
          if (!this.loadError()) {
            this.showNotFound();
          }
          return;
        }
        this.detail.set(detail);
        this.loadError.set(null);
        this.notFound.set(false);
        this.isLoading.set(false);
        this.loadPortalAccess(detail.patientId);
      });
  }

  /**
   * The subject patient's portal access state, for the chip beside their name.
   *
   * Read after the audit entry rather than alongside it, because the entry is what names the
   * patient to ask about. A failure is swallowed: the chip falls back to "Status unavailable" and
   * the audit entry — which is what this screen exists to show — stands on its own. What it must
   * never do is fall back to "Active", which is why {@link getAccessStatusBadge} has no such
   * default.
   */
  private loadPortalAccess(patientId: number): void {
    if (!Number.isInteger(patientId) || patientId <= 0) {
      this.accessLoadFailed.set(true);
      return;
    }

    this.portalAccessService
      .getPortalAccess(patientId)
      .pipe(
        catchError((error: unknown) => {
          logHttpError('Failed to load portal access for an audit entry', error);
          this.accessLoadFailed.set(true);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((access) => this.portalAccess.set(access));
  }

  private showNotFound(): void {
    this.notFound.set(true);
    this.loadError.set(NOT_FOUND_MESSAGE);
    this.isLoading.set(false);
  }

  /**
   * Project one disclosed record into a table row. The `appointment` payload is passed in already
   * narrowed by {@link rows}, so this cannot be called for a record that has none.
   *
   * Every absent value becomes the em-dash placeholder rather than a substitute: the two source
   * tables genuinely do not carry the same columns (a Meditech row has no appointment type, a
   * room-only native booking has no provider), and an audit trail must not show a patient
   * something they were never shown. `providerSpecialty` is the one exception — it is empty rather
   * than dashed, because no source has the column at all, so a dash on every row would advertise
   * missing data instead of an absent feature.
   */
  private toRow(record: DisclosedRecord, appointment: DisclosedAppointment): AppointmentRow {
    return {
      id: record.recordId,
      // Qualified by source, because `recordId` is only unique within its own table. See
      // `AppointmentRow.trackKey`.
      trackKey: `${appointment.isMeditechAppointment ? 'meditech' : 'native'}:${record.recordId}`,
      // Wall-clock, not UTC: these are the times the patient was shown, and the UTC helpers would
      // shift them by the browser's offset. See `formatWallClockDate`.
      date: formatWallClockDate(appointment.scheduledOnLocal) || EMPTY_CELL,
      time: formatWallClockTime(appointment.scheduledOnLocal),
      providerName: appointment.providerName ?? EMPTY_CELL,
      providerSpecialty: appointment.providerSpecialty ?? '',
      appointmentType: appointment.appointmentTypeName ?? EMPTY_CELL,
      locationName: appointment.locationName ?? EMPTY_CELL,
      locationDetail: appointment.locationDetail ?? '',
      // The precise status always renders, chip or no chip — the chip is a display grouping, the
      // label is the fact from the ledger.
      statusLabel: record.statusType ?? EMPTY_CELL,
      statusKind: getAppointmentStatusBadgeKind(appointment.statusCategory),
      isDeleted: record.isDeleted,
    };
  }
}
